import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  alpha,
  useTheme,
  CircularProgress,
} from "@mui/material";

import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Book as BookIcon,
} from "@mui/icons-material";

import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import apiEndpoints from "../../../apiconfig";

const StaffDetail = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  
  console.log("Staff GUID from params:", id);
const mapApiToUi = (row) => ({
  id: row.id,
  guid: row.guid,
  firstName: row.first_name || "",
  lastName: row.last_name || "",
  email: row.email || "",
  phone: row.mobile || "",
  qualification: row.qualification || "",
  experience: row.years_of_experience ?? 0,
  specialization: row.specialization || [],
  assignedClass: row.assigned_class || "",
  assignedClassName: row.assigned_class_name || "—",
  joiningDate: row.joining_date || "",
  profilePhoto: row.profile_photo || "",
  status: row.isActive === 1 ? "Active" : "Inactive",
  role: row.role || "",
  department: row.department || "",
  office: row.office || "",
  bio: row.bio || "No biography available.",
});


  // --- Fetch Single Staff from API ---
  const fetchStaffDetail = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiEndpoints.staff}?guid=${id}`);

      if (res.data.status === "success") {
        const record = mapApiToUi(res.data.data);
        setStaff(record);
      } else {
        setStaff(null);
      }
    } catch (error) {
      console.error("Error loading staff detail:", error);
      setStaff(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffDetail();
  }, [id]);

  // --- Loading State ---
  if (loading) {
    return (
      <Container>
        <Box sx={{ textAlign: "center", py: 10 }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading staff details...
          </Typography>
        </Box>
      </Container>
    );
  }

  // --- Staff Not Found ---
  if (!staff) {
    return (
      <Container>
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography variant="h6">Staff member not found</Typography>
          <Button
            onClick={() => navigate("/admin/staff-page")}
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
          >
            Back to Staff Directory
          </Button>
        </Box>
      </Container>
    );
  }

  const handleBack = () => {
    navigate("/admin/staff-page");
  };

  const handleEdit = () => {
    navigate(`/admin/staff/edit/${staff.guid}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 3 }}>
        Back to Staff Directory
      </Button>

      {/* Header Section */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "16px",
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={3}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Avatar
                src={staff.profilePhoto}
                sx={{
                  width: 200,
                  height: 200,
                  border: `6px solid ${theme.palette.background.paper}`,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={9}>
            <Grid container spacing={2} alignItems="flex-start" mb={2}>
              {/* Left content */}
              <Grid item xs={12} md={9}>
                <Typography variant="h3" fontWeight="700" gutterBottom sx={{textTransform: "uppercase"}}>
                  {staff.firstName} {staff.lastName}
                </Typography>

                <Typography variant="h5" color="primary" fontWeight="600">
                  {staff.role}
                </Typography>

                <Typography variant="h6" color="text.secondary">
                  {staff.department}
                </Typography>
              </Grid>

              {/* Status Chip */}
              <Grid
                item
                xs={12}
                md={3}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                <Chip
                  label={staff.status}
                  color={staff.status === "Active" ? "success" : "default"}
                  icon={staff.status === "Active" ? <CheckCircleIcon /> : null}
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    height: 32,
                    px: 2,
                  }}
                />
              </Grid>
            </Grid>

            {/* Contact Info */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <EmailIcon sx={{ mr: 2, color: "primary.main" }} />
                  <Typography>{staff.email}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <PhoneIcon sx={{ mr: 2, color: "primary.main" }} />
                  <Typography>{staff.phone}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                {/* <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <LocationIcon sx={{ mr: 2, color: "primary.main" }} />
                  <Typography>{staff.office}</Typography>
                </Box> */}

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CalendarIcon sx={{ mr: 2, color: "primary.main" }} />
                  <Typography>
                    Joined{" "}
                    {new Date(staff.joiningDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Edit Button (optional) */}
            {/* <Button variant="contained" startIcon={<EditIcon />} onClick={handleEdit} sx={{ mt: 3 }}>Edit Profile</Button> */}
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ borderRadius: "16px", mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider", px: 3, pt: 2 }}
        >
          <Tab label="Overview" />
          <Tab label="Experience" />
          <Tab label="Classes" />
        </Tabs>

        <Box sx={{ p: 4 }}>
          {/* TAB 1 – Overview */}
          {tabValue === 0 && (
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <Card sx={{ borderRadius: "12px" }}>
                  <CardContent>
                    {/* <Typography variant="h6" fontWeight="600">
                      Biography
                    </Typography>

                    <Typography color="text.secondary" paragraph>
                      {staff.bio}
                    </Typography> */}

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" fontWeight="600">
                      Specializations
                    </Typography>

                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}
                    >
                      {(staff.specialization || []).map((spec, idx) => (
                        <Chip
                          key={idx}
                          label={spec}
                          sx={{
                            background: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: "12px", mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600">
                      Qualification
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <SchoolIcon sx={{ mr: 2, color: "primary.main" }} />
                      <Typography>{staff.qualification}</Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Card sx={{ borderRadius: "12px" }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600">
                      Experience
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <WorkIcon sx={{ mr: 2, color: "primary.main" }} />
                      <Typography variant="h4" fontWeight="700">
                        {staff.experience}
                      </Typography>
                      <Typography sx={{ ml: 1 }}>years</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* TAB 2 – Experience */}
          {tabValue === 1 && (
            <Card sx={{ borderRadius: "12px" }}>
              <CardContent>
                <Typography variant="h6" fontWeight="600">
                  Professional Experience
                </Typography>

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <WorkIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${staff.role}`}
                      secondary={`${staff.experience} years of experience`}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CalendarIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Years of Service"
                      secondary={`Since ${new Date(
                        staff.joiningDate
                      ).getFullYear()}`}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <BookIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Areas of Expertise"
                      secondary={(staff.specialization || []).join(", ")}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          )}

          {/* TAB 3 – Classes */}
          {tabValue === 2 && (
            <Card sx={{ borderRadius: "12px" }}>
              <CardContent>
                <Typography variant="h6" fontWeight="600">
                  Assigned Classes
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {staff.assignedClassName &&
                  staff.assignedClassName !== "—" ? (
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper
                        sx={{
                          p: 3,
                          borderRadius: "12px",
                          textAlign: "center",
                          background: alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        <ClassIcon
                          sx={{ fontSize: 40, color: "primary.main", mb: 2 }}
                        />
                        <Typography variant="h6" fontWeight="600">
                          {staff.assignedClassName}
                        </Typography>
                        <Typography color="text.secondary">
                          Montessori Program
                        </Typography>
                      </Paper>
                    </Grid>
                  ) : (
                    <Typography color="text.secondary">
                      No class assigned
                    </Typography>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>
      </Paper>

      {/* Contact Information Card */}
      <Paper
        sx={{
          p: 4,
          borderRadius: "16px",
        }}
      >
        <Typography variant="h6" fontWeight="600">
          Contact Information
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <List>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Email" secondary={staff.email} />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <PhoneIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Phone" secondary={staff.phone} />
              </ListItem>
            </List>
          </Grid>

          <Grid item xs={12} md={6}>
            <List>
              {/* <ListItem>
                <ListItemIcon>
                  <LocationIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Office" secondary={staff.office} />
              </ListItem> */}

              <ListItem>
                <ListItemIcon>
                  <ScheduleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Office Hours"
                  secondary="Monday–Friday, 8:00 AM – 4:00 PM"
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default StaffDetail;
